import type { Catalog } from './types'

/**
 * The slim-catalogue cache (§15.3).
 *
 * The catalogue is ~160 KB of immutable metadata that changes only when an
 * admin edits something, so it is stored under a version key in localStorage
 * and revalidated with an ETag. A hit costs one trivial 304, which is what
 * keeps the Function bill inside the free grant.
 */

const KEY = 'itpu.catalog.v1'

interface CachedCatalog {
  version: number
  etag: string | null
  payload: Catalog
}

/** Mirrors the persisted copy so the UI still works when storage is unavailable. */
let memoryCache: CachedCatalog | null = null

export function readCachedCatalog(): CachedCatalog | null {
  if (memoryCache) return memoryCache
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedCatalog
    if (typeof parsed?.version !== 'number' || !Array.isArray(parsed?.payload?.books)) return null
    memoryCache = parsed
    return parsed
  } catch {
    // Corrupt entry, private-mode storage, or a quota error on read.
    return null
  }
}

export function writeCachedCatalog(payload: Catalog, etag: string | null): void {
  const entry: CachedCatalog = { version: payload.version, etag, payload }
  memoryCache = entry
  try {
    localStorage.setItem(KEY, JSON.stringify(entry))
  } catch {
    // QuotaExceededError or private mode — the in-memory copy is enough for
    // this session, we simply refetch on the next load.
  }
}

export function clearCachedCatalog(): void {
  memoryCache = null
  try {
    localStorage.removeItem(KEY)
  } catch {
    /* nothing to do */
  }
}
